// src/features/auth/forgotPassword.schema.ts
// Zod validation schemas for Forgot Password flow (Initiate, OTP Verification, and Password Reset).
import { z } from 'zod'

export const sanitizeEmail = (value: string): string => {
  return value.replace(/[^\x21-\x7E]/g, '')
}

export const sanitizeOtp = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 6)
}

export const sanitizePassword = (value: string): string => {
  return value.replace(/[^\x20-\x7E]/g, '')
}

/** Step 1: Initiate / Request OTP Schema */
export const initiateSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .trim()
    .email('Please enter a valid email address')
    .regex(/^[\x21-\x7E]*$/, 'Only English letters, numbers, and standard symbols are allowed'),
})

export type InitiateFormValues = z.infer<typeof initiateSchema>

/** Step 2: Verify OTP Schema */
export const verifyOtpSchema = z.object({
  otpCode: z
    .string()
    .min(1, 'Verification code is required')
    .trim()
    .length(6, 'Verification code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Verification code must be numbers only'),
})

export type VerifyOtpFormValues = z.infer<typeof verifyOtpSchema>

/** Step 3: Reset Password Schema */
export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'New password is required')
      .min(6, 'Password must be at least 6 characters')
      .regex(/^[\x20-\x7E]*$/, 'Only English letters, numbers, and standard symbols are allowed'),
    confirmPassword: z
      .string()
      .min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>
